// Shared date picker component used across Novala.
// Button shows DD/MM/YYYY display; clicking opens a calendar popover (CalendarGrid).
// API: value = ISO (YYYY-MM-DD), onChange = (iso) => void.
// Esc or outside click closes. Portal-rendered so it is not clipped by overflow.
// error prop: when set, the button shows a red border. The error TEXT is
// typically shown by the parent Field wrapper, not here.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon } from "lucide-react";
import CalendarGrid from "./CalendarGrid";
import { fmtDMY } from "./dateUtils";

interface DatePickerProps {
  value: string;
  onChange: (iso: string) => void;
  placeholder?: string;
  minYear?: number;
  maxYear?: number;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  error?: string;
}

export default function DatePicker({
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  minYear,
  maxYear,
  className = "",
  disabled = false,
  ariaLabel,
  error,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      setAnchorRect(btnRef.current.getBoundingClientRect());
    }
    setOpen((v) => !v);
  };

  const handleDaySelect = (iso: string) => {
    onChange(iso);
    setOpen(false);
  };

  const display = value ? fmtDMY(value) : "";
  const currentYear = new Date().getFullYear();
  const yMin = minYear ?? currentYear - 10;
  const yMax = maxYear ?? currentYear + 5;
  const borderClass = error ? "border-red-500" : "border-line";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        className={`inline-flex items-center justify-between w-full bg-white border rounded-md px-2 py-1 text-[12.5px] text-ink outline-none hover:border-[#B6BEC8] focus:border-teal-dark disabled:opacity-50 disabled:cursor-not-allowed ${borderClass} ${className}`}
      >
        <span className={display ? "text-ink" : "text-ink4"}>{display || placeholder}</span>
        <CalendarIcon size={14} className="text-ink4 ml-2 flex-shrink-0" aria-hidden="true" />
      </button>
      {open && anchorRect && createPortal(
        <div
          ref={popRef}
          style={{
            position: "fixed",
            top: anchorRect.bottom + 4,
            left: Math.min(anchorRect.left, window.innerWidth - 300),
            zIndex: 1000,
          }}
        >
          <CalendarGrid
            selectedIso={value}
            onSelect={handleDaySelect}
            minYear={yMin}
            maxYear={yMax}
          />
        </div>,
        document.body
      )}
    </>
  );
}